import { locale } from './zendesk.utils';
import { config } from '../../config';
import { isMobileQuery, languageQuery } from '@gig/magic-common';
import type { Language } from '@gig/magic-sdk/dist/adapter/users/language';

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        zE: any;
        zEACLoaded: boolean;
    }
}

export class ZendeskChatOnly {
    /**
     * Functions executed when the initial chat loading begins.
     */
    private listeners = new Set<() => any>();

    /**
     * Indicates if the chat has been loaded (script added to DOM, etc.).
     */
    isLoaded: boolean = false;

    /**
     * Indicates if loading above is in the process.
     */
    isLoading: boolean = false;

    /**
     * Sets Zendesk's language to the current language.
     */
    async setLanguage(language: Language) {
        window?.zE?.('messenger:set', 'locale', locale(language));
    }

    /**
     * Hides Zendesk by changing its z-index (underneath everything on mobile
     * devices and to level 0 on desktops).
     */
    async hide(): Promise<void> {
        const index = (await isMobileQuery()) ? -1 : 0;
        window?.zE?.('messenger:set', 'zIndex', index);
    }

    /**
     * Shows Zendesk by moving it to the highest possible z-index.
     */
    show(): void {
        window?.zE?.('messenger:set', 'zIndex', 999999);
    }

    /**
     * Closes Zendesk chat, so only the button stays.
     */
    close(): void {
        window?.zE?.('messenger', 'close');
    }

    /**
     * Opens Zendesk.
     */
    open(): void {
        window?.zE?.('messenger', 'open');
    }

    /**
     * Lazily loads Zendesk's script and returns a promise that is resolved
     * when Zendesk's iframe's 'load' event is emitted.
     */
    loadZendesk(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement(
                'script',
            ) as HTMLScriptElement;
            script.id = 'ze-snippet';
            script.src = config?.zendesk?.sdkUrl as string;
            script.onload = () => {
                const checker = setInterval(() => {
                    const iframe = document.querySelector(
                        'iframe[data-product="web_widget"]',
                    ) as HTMLIFrameElement | null;

                    if (iframe && window?.zEACLoaded) {
                        const onIframeLoad = () => {
                            iframe.removeEventListener('load', onIframeLoad);
                        };
                        iframe.addEventListener('load', onIframeLoad);
                        clearInterval(checker);
                        resolve();
                    }
                }, 4);
            };
            script.onerror = () => reject();
            document.body.appendChild(script);
        });
    }

    /**
     * A function that starts Zendesk and if it's not loaded, initiates lazy
     * loading. It also registers onClose handler.
     */
    async startingZendesk(): Promise<void> {
        if (this.isLoading) {
            return;
        }

        const language = await languageQuery();

        if (!window.zE) {
            try {
                this.isLoading = true;

                for (const listener of this.listeners) {
                    listener();
                }
                await this.loadZendesk();
                this.isLoaded = true;
            } catch (error) {
                console.error('Error loading Messenger');
                console.error(error);
            } finally {
                this.isLoading = false;
            }
        }

        this.setLanguage(language);
        this.open();
        this.show();
    }

    /**
     * Adds a listener to be fired when script loading starts.
     * @param fn A callback.
     * @returns function A function to remove the listener from the list.
     */
    onLoadingStart(fn: () => any): () => void {
        this.listeners.add(fn);

        return () => {
            this.listeners.delete(fn);
        };
    }
}
